import React, { useState, useEffect, useCallback } from 'react';
import { authAPI, isInLoginGracePeriod } from '../../services/api';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, Plus, Edit, Trash2, Search, Check, X } from 'lucide-react';
import { type Company } from '../../contexts/CompanyContext';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableNetworkError = (error: unknown): boolean => {
  const err = error as { message?: string; code?: string };
  const message = err.message?.toLowerCase() ?? '';
  return (
    message.includes('network error') ||
    err.code === 'ERR_NETWORK' ||
    err.code === 'ERR_NETWORK_CHANGED'
  );
};

export const CompaniesManagement: React.FC = () => {
  const { t } = useI18n();
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    email: '',
    phone: '',
    is_active: true,
  });

  const loadCompanies = useCallback(async () => {
    const maxAttempts = 3;
    try {
      setLoading(true);
      setError('');

      let response;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          response = await authAPI.get('/admin/companies');
          break;
        } catch (err) {
          if (attempt < maxAttempts - 1 && isRetryableNetworkError(err)) {
            await sleep(400 * (attempt + 1));
            continue;
          }
          throw err;
        }
      }

      if (!response) {
        throw new Error('Network Error');
      }

      // Garantir que sempre temos um array
      let companiesData: Company[] = [];
      if (Array.isArray(response.data)) {
        companiesData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Se vier como objeto, tentar extrair o array
        companiesData = response.data.companies || response.data.data || response.data.results || [];
      }

      // Garantir que é um array válido
      if (!Array.isArray(companiesData)) {
        console.warn('[CompaniesManagement] Companies data is not an array:', companiesData);
        companiesData = [];
      }

      setCompanies(companiesData);
    } catch (error: any) {
      const detail = error.response?.data?.error || error.message;
      const hint = isRetryableNetworkError(error)
        ? ' Verifique sua conexão ou clique em Tentar novamente.'
        : '';
      setError('Erro ao carregar empresas: ' + detail + hint);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      if (!localStorage.getItem('token')) {
        setLoading(false);
        return;
      }
      loadCompanies();
    }, isInLoginGracePeriod() ? 600 : 300);

    return () => clearTimeout(timer);
  }, [authLoading, isAuthenticated, token, loadCompanies]);

  const handleCreate = () => {
    setFormData({
      name: '',
      document: '',
      email: '',
      phone: '',
      is_active: true,
    });
    setShowCreateModal(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      document: company.document || '',
      email: company.email || '',
      phone: '',
      is_active: company.is_active,
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      if (editingCompany) {
        // TODO: Implementar endpoint de atualização
        await authAPI.put(`/admin/companies/${editingCompany.id}`, formData);
      } else {
        // TODO: Implementar endpoint de criação
        await authAPI.post('/admin/companies', formData);
      }
      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingCompany(null);
      loadCompanies();
    } catch (error: any) {
      setError('Erro ao salvar empresa: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta empresa?')) {
      return;
    }
    try {
      // TODO: Implementar endpoint de exclusão
      await authAPI.delete(`/admin/companies/${id}`);
      loadCompanies();
    } catch (error: any) {
      setError('Erro ao excluir empresa: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredCompanies = (Array.isArray(companies) ? companies : []).filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.document?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Gerenciamento de Empresas
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
            Gerencie todas as empresas do sistema
          </p>
        </div>
        <button
          onClick={handleCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          <Plus size={18} />
          Nova Empresa
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '16px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => loadCompanies()}
            style={{
              padding: '6px 12px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              whiteSpace: 'nowrap',
            }}
          >{t('billing.retryLoad')}</button>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ position: 'relative' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
            }}
          />
          <input
            type="text"
            placeholder={t('companies.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Companies List */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          Carregando empresas...
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          {searchTerm ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa cadastrada'}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px',
          }}
        >
          {filteredCompanies.map((company) => (
            <div
              key={company.id}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Building2 size={24} style={{ color: '#2563eb' }} />
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                      {company.name}
                    </h3>
                    {company.document && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                        {company.document}
                      </p>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    backgroundColor: company.is_active ? '#dcfce7' : '#fee2e2',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: company.is_active ? '#166534' : '#991b1b',
                  }}
                >
                  {company.is_active ? <Check size={12} /> : <X size={12} />}
                  {company.is_active ? 'Ativa' : 'Inativa'}
                </div>
              </div>

              {company.email && (
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  📧 {company.email}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                <button
                  onClick={() => handleEdit(company)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: '#374151',
                  }}
                >
                  <Edit size={14} />{t('actions.edit')}</button>
                <button
                  onClick={() => handleDelete(company.id)}
                  style={{
                    padding: '8px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    color: '#dc2626',
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px',
          }}
          onClick={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setEditingCompany(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>
              {editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Documento (CNPJ/CPF)
                </label>
                <input
                  type="text"
                  value={formData.document}
                  onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>{t('common.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span style={{ fontSize: '14px' }}>{t('companies.active')}</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setEditingCompany(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >{t('actions.cancel')}</button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingCompany ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
